import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import EditableHTML from '@v4/components/common/EditableHTML'
import SortHandler from '@v4/components/common/SortHandler'
import Image from '@v4/components/common/Image'
import ActionButton from '@v4/components/common/Buttons/ActionButton'
import PageMetaData from '@v4/utils/data/PageMetaData'
import EditableContainer from '@v4/components/common/EditableContainer'
import { CONTENT_DIRECTION, TEXT_POSITION } from '../../CallToAction.const'
import { useContent } from './Content.hooks'

const Content = props => {
	const [
		{
			classes,
			cx,
			sx,
			text,
			buttonText,
			buttonLink,
			additionalText,
			buttonAction,
			className,
			image,
			withImage,
			onTextClick,
			onButtonClick,
			onAdditionalClick,
			onImageClick,
			extraShortcuts,
			onClick,
			textEditable,
			additionalTextEditable,
			onTextChange,
			onAdditionalTextChange,
			additionalTextConfig,
			textConfig,
			buttonTextConfig,
			buttonTextEditable,
			onButtonTextChange,
			textKey,
			buttonTextKey,
			additionalTextKey,
			white,
			additionalPosition = TEXT_POSITION.TOP,
			isPaper = false,
			sortHandlerParentRef,
		},
	] = useContent(props)

	return (
		<Box
			ref={sortHandlerParentRef}
			className={cx(classes.root, classes[additionalPosition], className)}
			onClick={onClick}
		>
			<SortHandler
				parentRef={sortHandlerParentRef}
				defaultOrder={[1, 0]}
				direction={CONTENT_DIRECTION[additionalPosition]}
			>
				<Box display="flex" className={cx(classes.content, 'cta-content')}>
					{withImage && (
						<Image className={cx(classes.image, 'cta-image')} onClick={onImageClick} metaData={image} />
					)}
					<EditableContainer
						visible={!!text}
						fieldConfig={textConfig}
						usePaperColors={isPaper}
						onAdd={onTextChange}
						editable={textEditable}
						wrapper={({ children }) => (text ? children : <Box width="100%">{children}</Box>)}
					>
						<EditableHTML
							key={textKey}
							className={cx(classes.text, 'cta-text-block')}
							usePaperColors={isPaper}
							html={text}
							onClick={onTextClick}
							extraShortcuts={extraShortcuts}
							editable={textEditable}
							onChange={onTextChange}
							white={white}
							sx={sx}
						/>
					</EditableContainer>
					<EditableContainer
						visible={!!buttonText}
						fieldConfig={buttonTextConfig}
						usePaperColors={isPaper}
						onAdd={onButtonTextChange}
						editable={buttonTextEditable}
						defaultValue={buttonTextConfig?.default}
						label="Button"
						wrapper={({ children }) => (
							<Box
								width={buttonText ? null : '100%'}
								mt={3.2}
								className={cx(classes.buttonWrap, 'cta-button-wrapper')}
							>
								{children}
							</Box>
						)}
					>
						<ActionButton
							className={cx(classes.button, 'cta-button')}
							key={buttonTextKey}
							editable={buttonTextEditable}
							onClick={onButtonClick}
							onChange={onButtonTextChange}
							action={buttonAction}
							href={buttonLink}
						>
							{buttonText}
						</ActionButton>
					</EditableContainer>
				</Box>
				<Box width="100%">
					<EditableContainer
						className={cx(classes.additionalText)}
						visible={!!additionalText}
						fieldConfig={additionalTextConfig}
						onAdd={onAdditionalTextChange}
						editable={additionalTextEditable}
						usePaperColors={isPaper}
					>
						<EditableHTML
							key={additionalTextKey}
							className={cx(classes.additionalText, 'cta-text-block-additional')}
							usePaperColors={isPaper}
							html={additionalText}
							onClick={onAdditionalClick}
							editable={additionalTextEditable}
							onChange={onAdditionalTextChange}
							white={white}
							sx={sx}
						/>
					</EditableContainer>
				</Box>
			</SortHandler>
		</Box>
	)
}

Content.propTypes = {
	image: PropTypes.instanceOf(PageMetaData),
	text: PropTypes.node,
	buttonText: PropTypes.string,
	buttonLink: PropTypes.string,
	buttonAction: PropTypes.string,
	additionalText: PropTypes.node,
	additionalPosition: PropTypes.oneOfType([PropTypes.oneOf(Object.values(TEXT_POSITION))]),
	className: PropTypes.string,
	isPaper: PropTypes.bool,
	classes: PropTypes.shape({
		root: PropTypes.string,
		content: PropTypes.string,
		text: PropTypes.string,
		image: PropTypes.string,
		buttonWrap: PropTypes.string,
		button: PropTypes.string,
		additionalText: PropTypes.string,
	}),
	extraShortcuts: PropTypes.objectOf(PropTypes.string),
	textEditable: PropTypes.bool,
	onTextChange: PropTypes.func,
	additionalTextEditable: PropTypes.bool,
	onAdditionalTextChange: PropTypes.func,
	onButtonTextChange: PropTypes.func,
	buttonTextEditable: PropTypes.bool,
	textConfig: PropTypes.object,
	buttonTextConfig: PropTypes.object,
	additionalTextConfig: PropTypes.object,
	textKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	buttonTextKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	additionalTextKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mobileAlignment: PropTypes.string,
	mobileButtonWidth: PropTypes.string,
	white: PropTypes.bool,
	sx: PropTypes.shape({
		white: PropTypes.shape({
			lg: PropTypes.oneOf([false, true, 'theme']),
			md: PropTypes.oneOf([false, true, 'theme']),
			sm: PropTypes.oneOf([false, true, 'theme']),
			xs: PropTypes.oneOf([false, true, 'theme']),
		}),
		shadow: PropTypes.shape({
			lg: PropTypes.bool,
			md: PropTypes.bool,
			sm: PropTypes.bool,
			xs: PropTypes.bool,
		}),
	}),
}

export default memo(Content)
